import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '@/components/Footer';

const BG = '#0d0d1a';
const BG_ALT = '#0a0a18';
const CARD = '#13132a';
const GREEN = '#4ade80';
const ON_GREEN = '#0d0d1a';

const RevealOnScroll = ({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setTimeout(() => setVisible(true), delay * 100); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

const CONTACT_EMAILS = ['info@zoomlocal.in'];

const AgencyWebContactForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', businessName: '',
    locations: '1-10', plan: 'not-sure', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = (): string | null => {
    const n = form.name.trim();
    const em = form.email.trim().toLowerCase();
    const ph = form.phone.replace(/[\s\-()]/g, '');
    const biz = form.businessName.trim();
    const msg = form.message.trim();
    // Name
    if (n.length < 2) return 'Name must be at least 2 characters.';
    if (n.length > 100) return 'Name must be under 100 characters.';
    if (!/[a-zA-Z]/.test(n)) return 'Name must contain at least one letter.';
    // Email
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(em)) return 'Please enter a valid email address.';
    if (em.length > 150) return 'Email must be under 150 characters.';
    // Phone
    if (ph.length < 10) return 'Phone number must be at least 10 digits.';
    if (!/^\+?[\d]{10,15}$/.test(ph)) return 'Please enter a valid phone number (digits only, optional + prefix).';
    // Agency name
    if (biz.length < 2) return 'Agency name must be at least 2 characters.';
    if (biz.length > 200) return 'Agency name must be under 200 characters.';
    if (!/[a-zA-Z]/.test(biz)) return 'Agency name must contain at least one letter.';
    // Message
    if (msg.length > 1000) return 'Message must be under 1000 characters.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSubmitting(true);
    const trimmed = {
      ...form,
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      businessName: form.businessName.trim(),
      message: form.message.trim(),
    };
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-managed-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ ...trimmed, to: CONTACT_EMAILS, source: 'agencies-fly-web' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || `Server error (${res.status})`);
      }
      // GTM dataLayer event for conversion tracking
      (window as any).dataLayer?.push({
        event: 'agency_form_submit',
        source: 'agencies-fly-web',
      });

      const params = new URLSearchParams({ business: trimmed.businessName });
      navigate(`/agencies-fly-web/thank-you?${params.toString()}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.error('[AgenciesFlyWebPage] Contact form error:', msg);
      setError(msg === 'Failed to fetch'
        ? 'Network error. Please check your connection and try again.'
        : `Something went wrong: ${msg}. Please try again or reach us on WhatsApp.`);
    } finally {
      setSubmitting(false);
    }
  };


  const inp = 'w-full rounded-[10px] px-4 py-3 text-white text-sm outline-none transition-colors placeholder:text-white/30';
  const inpStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' };
  const lbl = 'block text-sm font-semibold mb-1.5';
  const lblStyle = { color: 'rgba(255,255,255,0.6)' };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={lbl} style={lblStyle}>Your Name</label>
          <input type="text" required maxLength={100} value={form.name} onChange={set('name')} className={inp} style={inpStyle} placeholder="Jane Doe" />
        </div>
        <div>
          <label className={lbl} style={lblStyle}>Email Address</label>
          <input type="email" required maxLength={150} value={form.email} onChange={set('email')} className={inp} style={inpStyle} placeholder="jane@youragency.com" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={lbl} style={lblStyle}>Phone Number</label>
          <input type="tel" required maxLength={18} value={form.phone} onChange={set('phone')} className={inp} style={inpStyle} placeholder="+91 98765 00000" />
        </div>
        <div>
          <label className={lbl} style={lblStyle}>Agency Name</label>
          <input type="text" required maxLength={200} value={form.businessName} onChange={set('businessName')} className={inp} style={inpStyle} placeholder="Your Agency" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={lbl} style={lblStyle}>Number of Client Locations</label>
          <select value={form.locations} onChange={set('locations')} className={inp} style={inpStyle}>
            <option value="1-10" className="bg-[#13132a] text-white">1–10 locations</option>
            <option value="11-30" className="bg-[#13132a] text-white">11–30 locations</option>
            <option value="31-100" className="bg-[#13132a] text-white">31–100 locations</option>
            <option value="100+" className="bg-[#13132a] text-white">100+ locations</option>
          </select>
        </div>
        <div>
          <label className={lbl} style={lblStyle}>Plan You're Interested In</label>
          <select value={form.plan} onChange={set('plan')} className={inp} style={inpStyle}>
            <option value="starter" className="bg-[#13132a] text-white">Starter ($99/mo)</option>
            <option value="unlimited" className="bg-[#13132a] text-white">Unlimited ($299/mo)</option>
            <option value="saas-pro" className="bg-[#13132a] text-white">SaaS Pro ($499/mo)</option>
            <option value="not-sure" className="bg-[#13132a] text-white">Not sure yet</option>
          </select>
        </div>
      </div>
      <div>
        <label className={lbl} style={lblStyle}>Message (optional)</label>
        <textarea rows={4} maxLength={1000} value={form.message} onChange={set('message')}
          className={`${inp} resize-none`} style={inpStyle}
          placeholder="Tell us a bit about your agency and what you're looking for..." />
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <button type="submit" disabled={submitting}
        className="w-full py-4 rounded-[11px] text-base font-bold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
        style={{ background: GREEN, color: ON_GREEN, border: 'none' }}>
        {submitting ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  );
};

// ── Static data ───────────────────────────────────────────────────────────────

const STATS = [
  { n: '100+', label: 'Agencies & Freelancers\nOnboarded' },
  { n: '1,000+', label: 'Google Business\nProfiles Managed' },
  { n: '4.9★', label: 'Average Client\nSatisfaction Rating' },
  { n: '<24hrs', label: 'Average Setup\nTime to Go Live' },
];

const WHY_CARDS = [
  { icon: '🏷️', title: 'Your Brand Everywhere', desc: "Your logo, your colours, your domain. Clients see your brand — not ours. It's your platform." },
  { icon: '🗂️', title: 'Multi-Client Workspaces', desc: 'Manage all your clients from one organised dashboard. No switching accounts or juggling tabs.' },
  { icon: '📊', title: 'Branded Reports', desc: "Send professional PDF reports with your agency's branding. Clients receive polished monthly updates automatically." },
  { icon: '⚡', title: 'Full Platform Access', desc: 'Every feature — reviews, rankings, posts, SEO — available from day one. No feature gating, no add-ons.' },
];

const TESTIMONIALS = [
  { type: 'Agency', initials: 'RK', name: 'Rahul Jain', role: 'Healthcare Digital Agency · Mumbai', avBg: 'rgba(74,222,128,0.15)', avColor: '#4ade80', quote: '"We manage Google profiles for 18 clinics and diagnostic centres across Mumbai. Before Fly, calls were dropping because profiles had wrong timings and no posts. <em>Within 6 weeks, 4 of our clients reported a visible increase in inbound calls.</em> The white-label dashboard makes us look like a serious tech company."' },
  { type: 'Freelancer', initials: 'PM', name: 'Priya Kulkarni', role: 'Local SEO Freelancer · Pune', avBg: 'rgba(167,139,250,0.15)', avColor: '#a78bfa', quote: '"I was managing 9 salons and spas in Pune on my own — WhatsApp, spreadsheets, reminders. Fly changed everything. <em>Now I handle 22 clients and I actually have time to sleep.</em> Automated review alerts and scheduled posts saved me 15+ hours a week. My clients think I have a whole team behind me."' },
  { type: 'Agency', initials: 'AS', name: 'Arjun S.', role: 'Digital Marketing Agency · Bangalore', avBg: 'rgba(245,158,11,0.15)', avColor: '#f59e0b', quote: '"We launched our own branded Local SEO product using the SaaS Pro plan. Clients log in to our portal and pay us directly. <em>We added ₹75,000 in monthly recurring revenue within the first 3 months.</em> Best business decision we\'ve made this year."' },
  { type: 'Freelancer', initials: 'NV', name: 'Nisha Singh', role: 'Independent SEO Consultant · Delhi', avBg: 'rgba(96,165,250,0.15)', avColor: '#60a5fa', quote: '"My client is a chain of 5 luxury spas in Delhi. Their reviews were going unanswered and the profile had no photos or posts. <em>3 months in, they went from 3.8 to 4.6 stars and their Google calls doubled.</em> The branded monthly report makes my work look absolutely premium."' },
  { type: 'Agency', initials: 'TM', name: 'Tarun M', role: 'Healthcare Marketing Agency · Hyderabad', avBg: 'rgba(248,113,113,0.15)', avColor: '#f87171', quote: '"We specialise in digital marketing for dental clinics and doctors. Managing profiles across 30+ locations used to take half our team\'s time. <em>Fly cut that down by at least 60%.</em> The citation checker and keyword tools are things our clients specifically ask about now."' },
  { type: 'Freelancer', initials: 'SK', name: 'Swetha Kumaran', role: 'Freelance SEO Specialist · Chennai', avBg: 'rgba(45,212,191,0.15)', avColor: '#2dd4bf', quote: '"I manage Google profiles for restaurants and cafés in Chennai. My biggest problem was clients asking what I was actually doing for them. Now I send a branded Fly report every month. <em>Not a single client has churned since I moved to Fly.</em> The WhatsApp support is also incredibly responsive."' },
];

const FEATURES = [
  { icon: '⭐', title: 'Review Management', desc: 'Monitor, respond to, and generate reviews across all client locations from one place.' },
  { icon: '📍', title: 'Rank Tracking', desc: 'Track local keyword rankings on Google Maps and Search for every client location.' },
  { icon: '🛡️', title: 'Profile Protection', desc: "Get instant alerts when Google makes unauthorised changes to your clients' profiles." },
  { icon: '📅', title: 'Content Publishing', desc: 'Schedule and publish Google Posts with high-quality creatives to keep profiles active.' },
  { icon: '📈', title: 'Analytics & Reporting', desc: 'Monthly branded performance reports with insights, competitor data, and growth trends.' },
  { icon: '🌐', title: 'Microsites & Store Locators', desc: 'Create dedicated microsites and store locator pages for each client to boost local SEO.' },
  { icon: '🏢', title: 'Multi-Location Management', desc: 'Manage tens or hundreds of locations simultaneously with bulk actions and filters.' },
  { icon: '🔍', title: 'SEO Audit & Optimisation', desc: 'Identify and fix profile gaps — keywords, images, services, categories, and Q&A.' },
  { icon: '🎨', title: 'White-Label Branding', desc: 'Full white-label setup with your logo, custom domain, and branded client portal.' },
];

const COMPARISON = [
  ['Juggling 5–10 tools just to manage one client', 'Everything in one white-label dashboard'],
  ['Manual review responses — missed alerts, unhappy clients', 'Automated alerts + one-click responses across all profiles'],
  ["Generic PDF reports clients don't value", 'Branded monthly reports clients actually read and appreciate'],
  ['Hours spent manually updating profiles and posting content', 'Scheduled posts and bulk management — set it and forget it'],
  ["No clear way to show clients what you're doing", 'Performance dashboards with competitor insights — full transparency'],
  ["Clients see your tools, not your brand", 'Your logo, your domain, your platform — 100% white-label'],
  ['Revenue capped by how many clients you can handle manually', 'Scale to unlimited clients with the same team, same effort'],
];

const ALL_PLANS = [
  'Full white-label branding', 'Custom domain support', 'Branded PDF client reports',
  'Multi-workspace management', 'GBP optimisation tools', 'Post scheduling & publishing',
  'Review management & responses', 'Rank tracking & analytics', 'Profile protection & alerts',
];

const STARTER_FEATURES = [
  'Review responder', 'Negative review alerts via WhatsApp & email',
  'Monthly performance & review stats', 'Post scheduling', 'Local citation check',
  'Profile change notifications', 'Branded client microsite', 'White-label portal & reports',
  'Your logo on dashboard', 'Team seats', 'Multi-location management', 'Priority email support',
];

const UNLIMITED_FEATURES = [
  { text: 'Everything in Starter', plus: false },
  { text: 'Unlimited locations — no cap', plus: true },
  { text: 'Unlimited team seats', plus: true },
  { text: 'Dedicated onboarding call', plus: true },
  { text: 'Chat + email support', plus: true },
  { text: 'Dedicated account manager', plus: true },
];

const PRO_FEATURES = [
  { text: 'Everything in Unlimited', purple: false },
  { text: 'Personalized payment gateway', purple: true },
  { text: 'Branded client landing + self-subscribe', purple: true },
  { text: 'MRR revenue dashboard', purple: true },
];

const PRO_TOOLS = ['🔍 GBP Audit Tool', '⭐ Review Analyzer', '🗒️ Citation Checker', '📱 Review QR Generator', '🌐 Microsite Builder'];

// ── Page ──────────────────────────────────────────────────────────────────────

const AgenciesFlyWebPage = () => (
  <>
    <Helmet>
      <title>Fly Web for Agencies | White-Label Local SEO Dashboard</title>
      <meta name="description" content="Your brand. Your clients. Fly's power. Manage all your clients' Google Business Profiles under your agency's brand — your logo, your domain, your revenue." />
      <meta property="og:title" content="Fly Web for Agencies | White-Label Local SEO Dashboard" />
      <meta property="og:description" content="Your brand, Fly's power. White-label GBP management dashboard for agencies." />
    </Helmet>

    <div className="min-h-screen text-white" style={{ background: BG }}>

      {/* Announcement bar */}
      <div className="flex flex-wrap items-center justify-center gap-2.5 px-5 py-2.5 text-center"
        style={{ background: 'rgba(74,222,128,0.12)', borderBottom: '1px solid rgba(74,222,128,0.2)' }}>
        <span className="text-xs font-bold tracking-wider" style={{ color: GREEN }}>🚀 Agency Mode is Live —</span>
        <a href="#contact" className="text-white font-bold text-xs underline">
          Start your 14-day free trial today. No credit card needed →
        </a>
      </div>

      {/* Stripped header — sticky Fly logo only, no nav links. Mirrors
          the focused-landing pattern from /lead-gen-tools so the page
          stays single-funnel without cross-navigation back into the
          marketing site. */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-10 pt-6 pointer-events-none">
        <a href="/" className="inline-flex items-center pointer-events-auto" aria-label="Fly — Web for Agencies">
          <img
            src="/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png"
            alt="Fly"
            className="h-16 md:h-20 brightness-0 invert drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
          />
        </a>
      </div>

      {/* Hero */}
      <div className="text-center px-6 py-20"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(74,222,128,0.08) 0%, transparent 65%)' }}>
        <RevealOnScroll>
          <div className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6"
            style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.3)', color: GREEN }}>
            For Agencies &amp; Freelancers
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-tight mb-5">
            White-Label Dashboard<br />for <span style={{ color: GREEN }}>Agencies</span>
          </h1>
          <p className="text-lg md:text-xl mx-auto mb-8 leading-relaxed max-w-[580px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Your brand. Your clients. Fly's power. Manage all your clients' Google Business Profiles under your agency's brand — your logo, your domain, your revenue.
          </p>
          <div className="flex flex-wrap gap-3.5 justify-center items-center mb-3">
            <a href="#contact">
              <button className="px-8 py-4 rounded-[10px] font-bold text-base cursor-pointer hover:opacity-90 transition-opacity"
                style={{ background: GREEN, color: ON_GREEN, border: 'none' }}>
                Start A 14 Day Free Trial
              </button>
            </a>
            <a href="#features">
              <button className="px-7 py-4 rounded-[10px] text-base cursor-pointer bg-transparent text-white hover:bg-white/10 transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.25)' }}>
                See How It Works
              </button>
            </a>
          </div>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>No credit card required · Cancel anytime</p>
        </RevealOnScroll>
      </div>

      {/* Stats bar */}
      <div className="px-10 py-8"
        style={{ background: BG_ALT, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1000px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-5 text-center">
          {STATS.map((s, i) => (
            <RevealOnScroll key={i} delay={i}>
              <div className="text-4xl font-black leading-none mb-2 tracking-tighter" style={{ color: GREEN }}>{s.n}</div>
              <div className="text-xs leading-relaxed whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.5)' }}>{s.label}</div>
            </RevealOnScroll>
          ))}
        </div>
      </div>

      {/* Why agencies */}
      <div className="max-w-[1100px] mx-auto px-10 py-20">
        <RevealOnScroll>
          <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: GREEN }}>Why Agencies Choose Us</div>
          <div className="text-4xl font-extrabold tracking-tight mb-3">Your Brand, Fly's Power</div>
          <p className="text-lg mb-12 max-w-[560px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Get your own white-labelled Local SEO platform — fully branded and ready to impress clients from day one.
          </p>
        </RevealOnScroll>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_CARDS.map((c, i) => (
            <RevealOnScroll key={i} delay={i}>
              <div className="rounded-2xl p-7" style={{ background: CARD, border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="text-3xl mb-4">{c.icon}</div>
                <h3 className="text-base font-bold mb-2">{c.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{c.desc}</p>
              </div>
            </RevealOnScroll>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div style={{ background: BG_ALT, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1100px] mx-auto px-10 py-20">
          <RevealOnScroll>
            <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: GREEN }}>What Our Clients Say</div>
            <div className="text-4xl font-extrabold tracking-tight mb-3">Agencies Are Already Growing With Fly</div>
            <p className="text-lg mb-10 max-w-[560px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
              From solo freelancers to full-service agencies — here's how they're using Fly to scale.
            </p>
          </RevealOnScroll>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <RevealOnScroll key={i} delay={i % 3}>
                <div className="flex flex-col gap-3.5 rounded-[18px] p-7 h-full"
                  style={{ background: CARD, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div>
                    <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full mr-2"
                      style={t.type === 'Agency'
                        ? { background: 'rgba(74,222,128,0.12)', color: GREEN, border: '1px solid rgba(74,222,128,0.25)' }
                        : { background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }
                      }>{t.type}</span>
                    <span className="text-sm" style={{ color: '#f59e0b' }}>★★★★★</span>
                  </div>
                  <p className="text-sm leading-7 italic flex-grow [&_em]:not-italic [&_em]:font-semibold [&_em]:text-green-400"
                    style={{ color: 'rgba(255,255,255,0.72)' }}
                    dangerouslySetInnerHTML={{ __html: t.quote }} />
                  <div className="flex items-center gap-3 pt-3.5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
                      style={{ background: t.avBg, color: t.avColor }}>{t.initials}</div>
                    <div>
                      <div className="text-sm font-bold text-white">{t.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{t.role}</div>
                    </div>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </div>

      {/* Platform features */}
      <div id="features" style={{ background: BG_ALT }}>
        <div className="max-w-[1100px] mx-auto px-10 py-20">
          <RevealOnScroll>
            <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: GREEN }}>Platform Features</div>
            <div className="text-4xl font-extrabold tracking-tight mb-3">Everything You Need to Deliver Results</div>
            <p className="text-lg mb-12 max-w-[560px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Nine powerful capabilities built into every plan — so you can manage every part of your clients' local presence.
            </p>
          </RevealOnScroll>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <RevealOnScroll key={i} delay={i % 3}>
                <div className="flex items-start gap-3.5 rounded-2xl p-6"
                  style={{ background: CARD, border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="w-10 h-10 rounded-[10px] flex items-center justify-center text-lg flex-shrink-0"
                    style={{ background: 'rgba(74,222,128,0.12)' }}>{f.icon}</div>
                  <div>
                    <h3 className="text-sm font-bold mb-1.5">{f.title}</h3>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{f.desc}</p>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison */}
      <div style={{ background: BG, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1000px] mx-auto px-10 py-20">
          <RevealOnScroll>
            <div className="text-xs font-bold tracking-widest uppercase mb-3 text-center" style={{ color: GREEN }}>Why Fly</div>
            <div className="text-4xl font-extrabold tracking-tight mb-3 text-center">Fly vs. Doing It Manually</div>
            <p className="text-lg text-center mx-auto max-w-[560px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
              The difference between agencies that scale and agencies that stay stuck is usually one thing — the right platform.
            </p>
          </RevealOnScroll>
          <div className="mt-10 rounded-2xl overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="px-7 py-4 text-sm font-black text-left w-1/2"
                    style={{ background: 'rgba(239,68,68,0.1)', color: 'rgba(255,255,255,0.6)', borderBottom: '1px solid rgba(239,68,68,0.2)' }}>
                    😓 Without Fly Social
                  </th>
                  <th className="px-7 py-4 text-sm font-black text-left"
                    style={{ background: 'rgba(74,222,128,0.1)', color: GREEN, borderBottom: '1px solid rgba(74,222,128,0.2)' }}>
                    ✅ With Fly Social
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([without, withFly], i) => (
                  <tr key={i}>
                    <td className="px-7 py-4 text-sm leading-relaxed align-top"
                      style={{ color: 'rgba(255,255,255,0.45)', background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ color: '#f87171', fontWeight: 700 }}>✗&nbsp;&nbsp;</span>{without}
                    </td>
                    <td className="px-7 py-4 text-sm leading-relaxed align-top"
                      style={{ color: 'rgba(255,255,255,0.82)', background: i % 2 === 0 ? 'rgba(74,222,128,0.04)' : 'rgba(74,222,128,0.02)' }}>
                      <span style={{ color: GREEN, fontWeight: 700 }}>✓&nbsp;&nbsp;</span>{withFly}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div style={{ background: BG }}>
        <div className="max-w-[1160px] mx-auto px-10 py-20 pb-24">
          <RevealOnScroll>
            <div className="text-center mb-12">
              <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: GREEN }}>Pricing</div>
              <div className="text-4xl font-extrabold tracking-tight mb-2">Simple Plans. No Hidden Fees.</div>
              <p className="text-lg" style={{ color: 'rgba(255,255,255,0.5)' }}>Start free for 14 days. No credit card required. Cancel anytime.</p>
            </div>
          </RevealOnScroll>
          <div className="grid md:grid-cols-3 gap-5 items-start">

            {/* Starter */}
            <RevealOnScroll delay={0}>
              <div className="rounded-[20px] p-8 flex flex-col" style={{ background: CARD, border: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="inline-block text-xs font-black tracking-widest uppercase px-3.5 py-1.5 rounded-full mb-6 w-fit"
                  style={{ background: '#f59e0b', color: ON_GREEN }}>Starter</span>
                <div className="mb-1.5">
                  <span className="text-xl font-bold align-super leading-none">$</span>
                  <span className="text-5xl font-black tracking-tight leading-none">99</span>
                  <span className="text-base ml-1" style={{ color: 'rgba(255,255,255,0.45)' }}>/mo</span>
                </div>
                <div className="text-sm font-semibold mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>📍 20 Locations</div>
                <p className="text-sm leading-relaxed mb-6 pb-6" style={{ color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  For freelancers getting started. Manage up to 20 client locations with every feature included.
                </p>
                <ul className="flex flex-col gap-3 mb-7 flex-grow">
                  {STARTER_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.82)' }}>
                      <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5"
                        style={{ background: 'rgba(74,222,128,0.15)', color: GREEN }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#contact">
                  <button className="w-full py-3.5 rounded-[11px] text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity mb-2.5"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                    Start A 14 Day Free Trial
                  </button>
                </a>
                <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>No credit card required</p>
              </div>
            </RevealOnScroll>

            {/* Unlimited */}
            <RevealOnScroll delay={1}>
              <div className="rounded-[20px] p-8 flex flex-col md:-mt-2.5"
                style={{ background: '#1a1a3e', border: `1px solid ${GREEN}`, boxShadow: '0 0 40px rgba(74,222,128,0.12)' }}>
                <span className="inline-block text-xs font-black tracking-widest uppercase px-3.5 py-1.5 rounded-full mb-6 w-fit"
                  style={{ background: GREEN, color: ON_GREEN }}>Unlimited</span>
                <div className="mb-1.5">
                  <span className="text-xl font-bold align-super leading-none">$</span>
                  <span className="text-5xl font-black tracking-tight leading-none">299</span>
                  <span className="text-base ml-1" style={{ color: 'rgba(255,255,255,0.45)' }}>/mo</span>
                </div>
                <div className="text-sm font-semibold mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>∞ Unlimited Locations</div>
                <p className="text-sm leading-relaxed mb-6 pb-6" style={{ color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  No limits. Manage as many client locations as you want for one flat monthly price.
                </p>
                <ul className="flex flex-col gap-3 mb-7 flex-grow">
                  {UNLIMITED_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.82)' }}>
                      <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-sm"
                        style={{ background: 'rgba(74,222,128,0.2)', color: GREEN }}>{f.plus ? '+' : '✓'}</span>
                      {f.text}
                    </li>
                  ))}
                </ul>
                <a href="#contact">
                  <button className="w-full py-3.5 rounded-[11px] text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity mb-2.5"
                    style={{ background: GREEN, color: ON_GREEN, border: 'none' }}>
                    Start A 14 Day Free Trial
                  </button>
                </a>
                <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>No credit card required</p>
              </div>
            </RevealOnScroll>

            {/* SaaS Pro */}
            <RevealOnScroll delay={2}>
              <div className="rounded-[20px] p-8 flex flex-col" style={{ background: '#14102e', border: '1px solid rgba(167,139,250,0.35)' }}>
                <span className="inline-block text-xs font-black tracking-widest uppercase px-3.5 py-1.5 rounded-full mb-6 w-fit"
                  style={{ background: '#a78bfa', color: ON_GREEN }}>SaaS Pro</span>
                <div className="mb-1.5">
                  <span className="text-xl font-bold align-super leading-none">$</span>
                  <span className="text-5xl font-black tracking-tight leading-none">499</span>
                  <span className="text-base ml-1" style={{ color: 'rgba(255,255,255,0.45)' }}>/mo</span>
                </div>
                <div className="text-sm font-semibold mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>∞ Unlimited Locations</div>
                <p className="text-sm leading-relaxed mb-6 pb-6" style={{ color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  Run your own branded SaaS business. Your clients pay <strong className="text-white">you</strong> directly through your portal.
                </p>
                <ul className="flex flex-col gap-3 mb-5">
                  {PRO_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.82)' }}>
                      <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-sm"
                        style={{ background: f.purple ? 'rgba(167,139,250,0.2)' : 'rgba(74,222,128,0.15)', color: f.purple ? '#a78bfa' : GREEN }}>
                        {f.purple ? '+' : '✓'}
                      </span>
                      {f.text}
                    </li>
                  ))}
                </ul>
                <div className="rounded-2xl p-5 mb-5"
                  style={{ background: 'rgba(8,6,24,0.8)', border: '1px solid rgba(167,139,250,0.2)' }}>
                  <div className="text-xs font-black tracking-wider uppercase mb-2.5" style={{ color: '#a78bfa' }}>
                    🚀 Turn Your Website Into A Lead Machine
                  </div>
                  <p className="text-xs leading-relaxed mb-3.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    We embed 5 free tools on your branded website. Visitors use them for free — and since the tools show why their local SEO needs work, they reach out to <strong className="text-white">you</strong> to fix it. Organic leads, zero ad spend.
                  </p>
                  <div className="text-xs font-bold tracking-wider uppercase mb-2.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    🎁 5 Free Tools — Included
                  </div>
                  <ul className="flex flex-col gap-2">
                    {PRO_TOOLS.map((tool, i) => (
                      <li key={i} className="text-xs" style={{ color: 'rgba(255,255,255,0.72)' }}>{tool}</li>
                    ))}
                  </ul>
                  <p className="mt-3.5 text-xs font-semibold" style={{ color: '#f59e0b' }}>⚡ Every visitor becomes a warm lead — no ads needed.</p>
                </div>
                <a href="#contact">
                  <button className="w-full py-3.5 rounded-[11px] text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity mb-2.5"
                    style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.35)' }}>
                    Start A 14 Day Free Trial
                  </button>
                </a>
                <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>No credit card required</p>
              </div>
            </RevealOnScroll>

          </div>
        </div>
      </div>

      {/* All plans include */}
      <div style={{ background: BG_ALT }}>
        <div className="max-w-[1000px] mx-auto px-10 py-[70px] text-center">
          <RevealOnScroll>
            <h2 className="text-3xl font-extrabold mb-2.5">All Plans Include</h2>
            <p className="text-base mb-10" style={{ color: 'rgba(255,255,255,0.5)' }}>
              No feature gating. No surprises. Everything you need is included from day one.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 text-left">
              {ALL_PLANS.map((item, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-4 text-sm"
                  style={{ background: CARD, border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.78)' }}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: GREEN }} />
                  {item}
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
      </div>

      {/* CTA block */}
      <div className="text-center px-10 py-20"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(74,222,128,0.08) 0%, transparent 70%)', borderTop: '1px solid rgba(74,222,128,0.1)', borderBottom: '1px solid rgba(74,222,128,0.1)' }}>
        <RevealOnScroll>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight leading-tight mb-3">
            Ready to Build Your Agency<br />on <span style={{ color: GREEN }}>Fly</span>?
          </h2>
          <p className="text-lg mx-auto mb-8 max-w-[500px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Join 100+ agencies and freelancers already managing 1,000+ profiles. Start free, scale fast.
          </p>
          <a href="#contact">
            <button className="px-10 py-4 rounded-[10px] text-lg font-bold cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: GREEN, color: ON_GREEN, border: 'none' }}>
              Start A 14 Day Free Trial
            </button>
          </a>
          <p className="text-xs mt-3.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
            No credit card required · Set up in under 24 hours · Cancel anytime
          </p>
        </RevealOnScroll>
      </div>

      {/* Contact */}
      <div id="contact" className="max-w-[780px] mx-auto px-10 py-20 text-center">
        <RevealOnScroll>
          <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: GREEN }}>Get In Touch</div>
          <h2 className="text-4xl font-extrabold mb-3">Still Have Questions?</h2>
          <p className="text-lg mb-10 mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Tell us about your agency and we'll help you find the right plan. Or just start your free trial directly — no commitment needed.
          </p>
          <div className="rounded-[20px] p-10 text-left" style={{ background: CARD, border: '1px solid rgba(255,255,255,0.08)' }}>
            <AgencyWebContactForm />
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 px-5 py-4 rounded-xl text-sm"
            style={{ background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)', color: 'rgba(255,255,255,0.7)' }}>
            💬 Prefer WhatsApp? Chat with us at{' '}
            <a href="https://wa.me/918446712833" style={{ color: GREEN, fontWeight: 700, textDecoration: 'none' }}>
              +91 84467 12833
            </a>
          </div>
        </RevealOnScroll>
      </div>

      <Footer variant="dark" />
    </div>
  </>
);

export default AgenciesFlyWebPage;
